import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Outlet, NavLink } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui-kit";
import BadgeCelebration from "@/components/BadgeCelebration";
import OnboardingTour from "@/components/OnboardingTour";
import { STUDENT_STEPS } from "@/components/onboardingSteps";
import { Home, Map, Layers, MessagesSquare, BarChart3, Settings as SettingsIcon, ChevronLeft, HelpCircle } from "lucide-react";
import { gradeShort } from "@/config";

const bandClass = (band) => (band === "K-2" ? "band-k2" : band === "6-12" ? "band-6-12" : "band-3-5");

const NAV = [
  { to: "today", label: "Today", icon: Home },
  { to: "path", label: "Path", icon: Map },
  { to: "review", label: "Review", icon: Layers },
  { to: "tutor", label: "Tutor", icon: MessagesSquare },
  { to: "progress", label: "Progress", icon: BarChart3 },
];

export default function StudentShell() {
  const { studentId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(false);
  const [celebrateQueue, setCelebrateQueue] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const celebratedRef = useRef(null);
  const storageKey = `ns_celebrated_${studentId}`;
  // After a diagnostic signup we land on /today?from=diagnostic — don't pop the
  // tour over the first win. Captured once so it stays suppressed as they navigate.
  const [suppressTour] = useState(() => new URLSearchParams(loc.search).get("from") === "diagnostic");

  const reload = useCallback(() => {
    return api.get(`/students/${studentId}`).then(({ data }) => setStudent(data)).catch(() => setError(true));
  }, [studentId]);

  useEffect(() => { reload(); }, [reload]);

  const celebrateBadges = useCallback((badges) => {
    if (!celebratedRef.current || !badges || !badges.length) return;
    const fresh = badges.filter((b) => !celebratedRef.current.has(b.key));
    if (!fresh.length) return;
    fresh.forEach((b) => celebratedRef.current.add(b.key));
    localStorage.setItem(storageKey, JSON.stringify([...celebratedRef.current]));
    setCelebrateQueue(fresh);
  }, [storageKey]);

  const checkBadges = useCallback(async () => {
    if (!celebratedRef.current) return;
    try {
      const { data } = await api.get(`/students/${studentId}/badges`);
      celebrateBadges(data);
    } catch { /* ignore */ }
  }, [studentId, celebrateBadges]);

  // Baseline: existing badges are marked seen (no confetti) the first time this device opens the space.
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      celebratedRef.current = new Set(JSON.parse(stored));
    } else {
      celebratedRef.current = new Set();
      api.get(`/students/${studentId}/badges`).then(({ data }) => {
        data.forEach((b) => celebratedRef.current.add(b.key));
        localStorage.setItem(storageKey, JSON.stringify([...celebratedRef.current]));
      }).catch(() => {});
    }
  }, [studentId, storageKey]);

  // Catch badges earned during review/drill/tutor when the learner next navigates.
  useEffect(() => { checkBadges(); }, [loc.pathname, checkBadges]);

  if (error) return <div className="min-h-screen grid place-items-center text-[var(--ns-muted)]">Student not found.</div>;
  if (!student) return <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]"><Spinner label="Opening space" /></div>;

  const settings = student.settings || {};
  const wrapperClass = `${bandClass(student.grade_band)} ${settings.high_contrast ? "hc" : ""} ${settings.dyslexia_font ? "dys" : ""}`;
  const canGoBack = user.role !== "student";

  return (
    <div className={`${wrapperClass} min-h-screen bg-[var(--ns-bg)] text-[var(--ns-text)]`} data-testid="student-shell">
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-[var(--ns-border)]" style={{ background: "var(--ns-surface)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {canGoBack && (
              <button data-testid="back-to-parent" onClick={() => nav("/parent")} className="p-1 -ml-1 text-[var(--ns-muted)]"><ChevronLeft size={22} /></button>
            )}
            <div className="w-8 h-8 rounded-lg bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold text-sm shrink-0">
              {student.name[0]}
            </div>
            <div className="min-w-0">
              <div className="font-head font-bold text-sm leading-tight truncate">{student.name}</div>
              <div className="text-xs text-[var(--ns-subtle)]">{gradeShort(student.grade)}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="student-how-it-works" onClick={() => setShowTour(true)} className="p-2 text-[var(--ns-muted)] hover:text-[var(--ns-primary)]" title="How it works" aria-label="How it works">
              <HelpCircle size={20} />
            </button>
            <button data-testid="open-settings" onClick={() => nav(`/student/${studentId}/settings`)} className="p-2 text-[var(--ns-muted)] hover:text-[var(--ns-primary)]" aria-label="Settings">
              <SettingsIcon size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 pb-28 ns-scroll">
        <Outlet context={{ student, reload, studentId, celebrateBadges, checkBadges }} />
      </main>

      <BadgeCelebration badges={celebrateQueue} onClose={() => setCelebrateQueue([])} />
      <OnboardingTour steps={STUDENT_STEPS} storageKey={`ns_tour_student_${studentId}`} forceOpen={showTour} suppressAuto={suppressTour}
        onClose={() => {
          setShowTour(false);
          if (student && !student.diagnostic_done && !loc.pathname.endsWith("/diagnostic")) {
            nav(`/student/${studentId}/diagnostic`);
          }
        }} />

      <nav className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md border-t border-[var(--ns-border)]" style={{ background: "var(--ns-surface)" }} data-testid="bottom-nav">
        <div className="max-w-3xl mx-auto px-2 py-2 grid grid-cols-5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to}`}
              className="flex flex-col items-center gap-0.5 py-1 rounded-lg"
              style={({ isActive }) => ({ color: isActive || loc.pathname.includes(`/${n.to}`) ? "var(--ns-primary)" : "var(--ns-subtle)" })}>
              <n.icon size={22} />
              <span className="text-[11px] font-semibold">{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
